import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

const PAYMENT_METHODS = ['cash_on_delivery', 'cash_on_pickup', 'cash_advance', 'bank_transfer', 'cheque', 'credit_15d', 'credit_30d', 'credit_45d', 'credit_60d', 'credit_90d', 'other'] as const;

interface Lite { id: string; label: string }

interface RecordCollectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flatbedTripId?: string;
  tripId?: string;
  onRecorded?: () => void;
}

/**
 * PR MAY10 P4B-POLISH (A.1) — embedded into FlatbedTripDetailPage Financials
 * tab and OverdueCollectionsPage. Posts to /api/v1/payment-collections.
 */
export function RecordCollectionModal({ open, onOpenChange, flatbedTripId, tripId, onRecorded }: RecordCollectionModalProps) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<Lite[]>([]);
  const [drivers, setDrivers] = useState<Lite[]>([]);
  const [collectorType, setCollectorType] = useState<'user' | 'driver'>('user');
  const [collectorId, setCollectorId] = useState('');
  const [amount, setAmount] = useState('');
  const [collectedAt, setCollectedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [paymentMethod, setPaymentMethod] = useState<typeof PAYMENT_METHODS[number]>('bank_transfer');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get<{ items?: Array<{ id: string; fullNameEn?: string | null; full_name_en?: string | null }> }>('/api/v1/users?pageSize=200').then((r) => {
      setUsers((r.data.items ?? []).map(u => ({ id: u.id, label: u.fullNameEn ?? u.full_name_en ?? u.id })));
    }).catch(() => setUsers([]));
    apiClient.get<{ items?: Array<{ id: string; fullNameEn?: string | null }> }>('/api/v1/drivers?status=active&pageSize=200').then((r) => {
      setDrivers((r.data.items ?? []).map(d => ({ id: d.id, label: d.fullNameEn ?? d.id })));
    }).catch(() => setDrivers([]));
  }, [open]);

  function reset() {
    setAmount(''); setReferenceNumber(''); setBankName(''); setDestination(''); setNotes('');
    setCollectorId(''); setPaymentMethod('bank_transfer');
    setCollectedAt(new Date().toISOString().slice(0, 16));
  }

  async function submit() {
    if (!amount || Number(amount) <= 0) { window.alert(t('collection.amountRequired', { defaultValue: 'Amount must be greater than 0' })); return; }
    if (!collectorId) { window.alert(t('collection.collectorRequired', { defaultValue: 'Pick the person who collected the payment' })); return; }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        amountSar: Number(amount),
        collectedAt: new Date(collectedAt).toISOString(),
        paymentMethod,
        ...(flatbedTripId ? { flatbedTripId } : {}),
        ...(tripId        ? { tripId        } : {}),
        ...(collectorType === 'user'   ? { collectedByUserId:   collectorId } : {}),
        ...(collectorType === 'driver' ? { collectedByDriverId: collectorId } : {}),
        ...(referenceNumber ? { referenceNumber } : {}),
        ...(bankName        ? { bankName        } : {}),
        ...(destination     ? { destination     } : {}),
        ...(notes           ? { notes           } : {}),
      };
      await apiClient.post('/api/v1/payment-collections', payload);
      reset();
      onOpenChange(false);
      onRecorded?.();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  const collectorOptions = collectorType === 'user' ? users : drivers;
  const needsRef  = paymentMethod === 'bank_transfer' || paymentMethod === 'cheque';
  const needsBank = paymentMethod === 'bank_transfer';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t('collection.title', { defaultValue: 'Record cash collection' })}</SheetTitle>
          <SheetDescription dir="rtl">{t('collection.titleAr', { defaultValue: 'تسجيل تحصيل نقدي' })}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
          <div className="space-y-1"><Label>{t('collection.amount', { defaultValue: 'Amount (SAR) *' })}</Label><Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div className="space-y-1"><Label>{t('collection.collectedAt', { defaultValue: 'Collected at *' })}</Label><Input type="datetime-local" value={collectedAt} onChange={(e) => setCollectedAt(e.target.value)} /></div>
          <div className="space-y-1">
            <Label>{t('collection.method', { defaultValue: 'Payment method *' })}</Label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as typeof PAYMENT_METHODS[number])}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('collection.collector', { defaultValue: 'Collector *' })}</Label>
            <div className="flex gap-3">
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" checked={collectorType === 'user'}   onChange={() => { setCollectorType('user');   setCollectorId(''); }} className="h-4 w-4" />
                {t('collection.user',   { defaultValue: 'User' })}
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" checked={collectorType === 'driver'} onChange={() => { setCollectorType('driver'); setCollectorId(''); }} className="h-4 w-4" />
                {t('collection.driver', { defaultValue: 'Driver' })}
              </label>
            </div>
            <select value={collectorId} onChange={(e) => setCollectorId(e.target.value)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
              <option value="">{t('collection.pickCollector', { defaultValue: 'Pick…' })}</option>
              {collectorOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          {needsRef && (
            <div className="space-y-1"><Label>{t('collection.referenceNumber', { defaultValue: 'Reference / Cheque #' })}</Label><Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} /></div>
          )}
          {needsBank && (
            <div className="space-y-1"><Label>{t('collection.bankName', { defaultValue: 'Bank' })}</Label><Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Al Rajhi, SNB, …" /></div>
          )}
          <div className="space-y-1"><Label>{t('collection.destination', { defaultValue: 'Destination (where the money landed)' })}</Label><Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="bank_jasr_main / cash_box_office / …" /></div>
          <div className="space-y-1"><Label>{t('collection.notes', { defaultValue: 'Notes' })}</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </div>
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} disabled={submitting}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={submitting || !amount || !collectorId}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('collection.record', { defaultValue: 'Record collection' })}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
